import { InfoCircleTwoTone } from "@ant-design/icons";
import { getEnvironment } from "@p0security/frontend-core";
import { AwsPartition } from "@p0security/integrations-aws-shared/util";
import { arnInfo } from "@p0security/integrations-aws-shared/util";
import { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  P0_CLUSTER_ROLE,
  P0_CLUSTER_ROLE_BINDING,
} from "@p0security/integrations-kubernetes-shared/constants";
import {
  KubernetesBaseConfig,
  KubernetesComponents,
  KubernetesIamWriteConfig,
  KubernetesIamWriteProxyConfig,
} from "@p0security/integrations-kubernetes-shared/types";
import { ConfigOf, Instructions } from "@p0security/shared/install/types";
import { toClientKey } from "@p0security/shared/integrations/resources/braekhus/index";
import { Typography } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import { CommandDisplay } from "components/Integrations/CommandDisplay";
import yaml from "js-yaml";
import { compact } from "lodash";
import { ReactNode } from "react";

import {
  P0EKSAutoModeStorageClass,
  P0PerimeterConfiguration,
  P0PerimeterDeployment,
  P0ProxyDeployment,
  P0ProxyVolumeClaim,
  P0_BOUNDARY_SERVICE,
} from "../constants";
import { K8sInstaller } from "./types";

const { Paragraph, Text } = Typography;

const envToTunnelHost = (environment: ReturnType<typeof getEnvironment>) => {
  return new URL(environment.braekhusUrl).host;
};

const awsCommands = {
  eksDriverCommand: (
    awsPartition: AwsPartition,
    region: string,
    clusterName: string
  ) =>
    region && clusterName
      ? `ADDON=$(aws eks list-addons --region=${region} --cluster-name ${clusterName} | jq '.addons | select(index("aws-ebs-csi-driver"))')
if [ -z "$ADDON" ]
then
eksctl utils associate-iam-oidc-provider --region=${region} --cluster=${clusterName} --approve && \
eksctl create iamserviceaccount \
--name ebs-csi-controller-sa \
--namespace kube-system \
--region ${region} \
--cluster ${clusterName} \
--attach-policy-arn arn:${awsPartition}:iam::aws:policy/service-role/AmazonEBSCSIDriverPolicy \
--approve \
--role-only \
--role-name AmazonEKS_EBS_CSI_DriverRole && \
eksctl create addon --name aws-ebs-csi-driver --region ${region} --cluster ${clusterName} --service-account-role-arn arn:${awsPartition}:iam::$(aws sts get-caller-identity --query Account --output text):role/AmazonEKS_EBS_CSI_DriverRole --force
fi`
      : `Please provide aws region of the cluster.`,
};

export const proxyCommand = (
  targetId: string,
  item: Partial<KubernetesIamWriteProxyConfig>,
  slug: string
) => {
  const clientId = toClientKey({
    orgSlug: slug,
    targetId,
  });

  const tunnelHost = envToTunnelHost(getEnvironment());

  const eksAutoModeEnabled =
    item.hosting?.type === "aws" && !!item.hosting?.autoModeEnabled;

  return `kubectl apply -f - <<EOF
apiVersion: v1
kind: Namespace
metadata:
  name: p0-security
---

${eksAutoModeEnabled ? yaml.dump(P0EKSAutoModeStorageClass(), { flowLevel: 3 }) : ""}
---

${
  item.hosting
    ? yaml.dump(P0ProxyVolumeClaim(item.hosting.type, eksAutoModeEnabled), {
        flowLevel: 3,
      })
    : ""
}
---

${yaml.dump(P0ProxyDeployment(clientId, item, tunnelHost), {
  flowLevel: 3,
})}
EOF`;
};

export const k8sCommand = (
  base: KubernetesBaseConfig
) => `kubectl apply -f - <<EOF
apiVersion: v1
kind: Namespace
metadata:
  name: p0-security

---

apiVersion: v1
kind: ServiceAccount
metadata:
  name: p0-service-account
  namespace: p0-security

---

apiVersion: v1
kind: Secret
metadata:
  name: p0-service-account-secret
  namespace: p0-security
  annotations:
    kubernetes.io/service-account.name: p0-service-account
type: kubernetes.io/service-account-token

---

${yaml.dump(P0_CLUSTER_ROLE, { flowLevel: 3 })}
---

${yaml.dump(P0_CLUSTER_ROLE_BINDING, { flowLevel: 3 })}
---

${yaml.dump(P0PerimeterDeployment(base.serverKey, base.serverCert), {
  flowLevel: 8,
})}
---

${yaml.dump(P0_BOUNDARY_SERVICE, { flowLevel: 4 })}
---

${yaml.dump(P0PerimeterConfiguration(base.caBundle), { flowLevel: 5 })}

EOF`;

const sharedCommands = (
  clusterName: string,
  item: Partial<KubernetesIamWriteConfig>,
  base: KubernetesBaseConfig,
  orgSlug: string
) => {
  const { connectivity } = item;
  return compact([
    connectivity?.type === "proxy"
      ? {
          header: <Paragraph>Setup braekhus proxy:</Paragraph>,
          command: proxyCommand(
            clusterName,
            { ...item, connectivity },
            orgSlug
          ),
        }
      : undefined,
    {
      header: (
        <Paragraph>
          Setup P0 admission controller, then click &quot;Next&quot;:
        </Paragraph>
      ),
      command: k8sCommand(base),
    },
  ]);
};

const providerInstructions = (
  hosting: KubernetesIamWriteConfig["hosting"]
): { helpInstruction?: JSX.Element } => {
  if (hosting?.type === "aws" && !hosting.autoModeEnabled) {
    return {
      helpInstruction: (
        <Paragraph>
          Provide the region and cluster name below for commands that enable the
          EBS CSI driver. See&nbsp;
          <Typography.Link
            href={
              "https://docs.aws.amazon.com/eks/latest/userguide/ebs-csi.html"
            }
          >
            Amazon EBS CSI driver
          </Typography.Link>
          &nbsp;documentation for more information.
        </Paragraph>
      ),
    };
  }
  return {};
};

const sharedInstructions = (
  component: FrontendInstallContext<ConfigOf<KubernetesComponents>>,
  id: string,
  item: KubernetesIamWriteConfig
) => {
  const { orgData } = component;
  const base = component.config.base[id];
  return {
    help: (
      <>
        <Paragraph>
          {item.connectivity?.type === "proxy" &&
            providerInstructions(item.hosting)?.helpInstruction}
        </Paragraph>
        <Paragraph>
          Run these kubectl commands to deploy the{" "}
          {item.connectivity?.type === "proxy" && <>braekhus proxy and</>} P0
          security perimeter{" "}
        </Paragraph>
      </>
    ),
    commands: {
      shell: compact([
        item.hosting?.type === "aws" &&
        !item.hosting.autoModeEnabled &&
        item.connectivity?.type === "proxy"
          ? {
              header: <Paragraph>Set up the AWS EKS CSI driver:</Paragraph>,
              // TODO: NO
              command: awsCommands.eksDriverCommand(
                arnInfo(item.hosting.arn).partition ?? "aws",
                arnInfo(item.hosting.arn).region ?? "",
                id
              ),
            }
          : undefined,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...sharedCommands(id, item, base, orgData!.slug),
      ]),
    },
  };
};

export const k8sInstaller: K8sInstaller = {
  components: {
    base: {
      items: {},
    },
    "iam-write": {
      instructions: (component, id, item): Instructions<ReactNode> => {
        const helper = sharedInstructions(component, id, item);

        return {
          help: helper?.help,
          commands: helper?.commands,
        };
      },
      items: {
        endpoint: {
          labeler: () => (
            <span>
              Server API endpoint&nbsp;
              <GraphTooltip
                title={
                  <ul>
                    <li>
                      AWS EKS:
                      <br />
                      Server API endpoint
                    </li>
                    <li>
                      Azure AKS:
                      <br />
                      API server address
                    </li>
                    <li>
                      GCloud GKE:
                      <br />
                      External or Internal endpoint
                    </li>
                  </ul>
                }
              >
                <InfoCircleTwoTone />
              </GraphTooltip>
            </span>
          ),
        },
        ca: {
          labeler: () =>
            "Find in the 'clusters' section of an existing client kube config file",
        },
        connectivity: {
          options: {
            proxy: {
              items: {
                publicJwk: {
                  labeler: () => (
                    <>
                      Access the pod with <Text code>kubectl</Text> and retrieve
                      the contents of the <Text code>jwk.public.json</Text>{" "}
                      file:
                      <br />
                      <CommandDisplay
                        commands={`kubectl exec -it deploy/p0-braekhus-proxy -n p0-security -c braekhus -- cat /p0-files/jwk.public.json | jq -Mr`}
                      />
                    </>
                  ),
                },
              },
            },
            public: {},
          },
        },
        hosting: {
          options: {
            aws: { items: { arn: {}, autoModeEnabled: {} } },
            azure: {},
            gcp: {
              items: {
                project: {},
                clusterName: {},
                location: {
                  options: {
                    region: {
                      items: {
                        region: {},
                      },
                    },
                    zone: {
                      items: {
                        zone: {},
                      },
                    },
                  },
                },
              },
            },
          },
        },
        token: {
          labeler: () => (
            <>
              Retrieve the token of the{" "}
              <Text code>p0-service-account-secret</Text> secret: <br />
              <CommandDisplay
                commands={`echo $(kubectl get secret p0-service-account-secret -o json -n p0-security | jq -r '.data.token' | base64 -d)`}
              />
            </>
          ),
        },
      },
    },
  },
  preamble: (
    <>
      <Paragraph>
        Enable fine-grained IAM management for Kubernetes clusters with secure
        access control via admission controller deployment.
      </Paragraph>
      <Text strong>Prerequisites:</Text>
      <ul className="preamble-list">
        <li>Access to a Kubernetes cluster on AWS, Azure, or Google Cloud</li>
        <li>
          <code>cluster-admin</code> role in the Kubernetes cluster
        </li>
        <li>
          <code>kubectl</code> and your cloud provider CLI installed
        </li>
        <li>P0 IAM integration installed for your cloud provider</li>
      </ul>
    </>
  ),
};
